import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import Tooltip from '@/components/Tooltip/Tooltip';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { FormattedMessage, useIntl } from 'react-intl';
import DeleteCircleIcon from '@/assets/delete-circle.svg?react';
import WarningTriangleIcon from '@/assets/warning-triangle.svg?react';
import {
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useUploadContext } from '@/contexts/UploadContext';
import {
  ComplianceDocument,
  FileType,
  PartnerSchema,
} from '@/lib/types';
import Select from '@/components/Select/Select';
import FilterChip from '@/components/ResourcesSidebar/FilterChip';
import {
  fetchComplianceDocumentsByCode,
  fetchPartners,
  fetchFilters,
  fetchGameById,
  fetchGames,
  postComplianceDocumentsPrecheck,
  postComplianceDocumentsUploadSessions,
} from '@/lib/api';
import DropZoneBig, {
  type PickedFile,
} from '@/components/DropZoneBig/DropZoneBig';
import {
  FileTypesComplianceGame,
  FileTypesComplianceMarket,
} from '@/constants/FileTypesCompliance';
import { ComplianceUploadAcceptedExtensions } from '@/constants/UploadAcceptedExtensions';
import {
  mapConfigToOptions,
  mapConstantsToOptions,
} from '@/lib/listOptions';
import { formatFileSize } from '@/lib/fileSize';
import { folderPartOf, withRenamedFile } from '@/lib/relativePath';
import { classifyUploadError } from '@/lib/uploadErrors';
import Markets from '@/constants/Markets';
import Languages from '@/constants/Languages';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import FileIcon from '@/components/FileExplorer/FileIcon';
import FileStatusBadge from './FileStatusBadge';
import EditableFileName, { renameFile } from './EditableFileName';
import {
  detectApprovalContent,
  detectCertificateDetails,
  detectFileJurisdiction,
  findCertificateCodeMismatch,
  languageForMarket,
  resolveCertificateCode,
} from './ComplianceDocumentsUploadModalHelpers';

const GAME_SELECT_FILE_TYPES = [
  'certificates',
  'approval',
  'rng_report',
  'isms_report',
  'rgs_platform_report',
];

const GAME_REQUIRED_FILE_TYPES = ['certificates'];

// File types whose games are set per file, instead of being picked
// once for the whole batch.
const PER_FILE_GAMES_FILE_TYPES = ['certificates', 'approval'];

// File types whose games are resolved from game identifiers in the
// file content.
const GAME_DETECTION_FILE_TYPES = ['certificates'];

// File types whose market is set per file, instead of once for the batch.
const PER_FILE_MARKET_FILE_TYPES = ['certificates', 'approval'];

const JURISDICTION_DETECTION_FILE_TYPES = ['certificates'];

// File types linked to the uploaded certificates whose codes their
// content quotes; market and games come from those certificates.
const CERTIFICATE_LINK_FILE_TYPES = ['approval'];

// File types whose certificate code is read from the file name or,
// failing that, the content.
const CERTIFICATE_CODE_FILE_TYPES = ['certificates'];

// How many not-ready files the Upload tooltip names before "and N more".
const NOT_READY_FILES_NAMED = 3;

type PrecheckStatus =
  'init' | 'loading' | 'unique' | 'duplicate' | 'error';

type UploadError = {
  status: 'duplicate' | 'error';
  message?: string;
};

type ValueSource = 'detected' | 'manual';

type LinkedCertificate = Pick<
  ComplianceDocument,
  'id' | 'name' | 'code'
>;

type FileListItem = {
  id: string;
  file: File;
  /** Path inside the picked folder (`C/d.txt`). */
  relativePath: string;
  // Sticky, content-based result of scanning a PDF for game identifiers.
  // Never changes once set — a rename doesn't change the file's bytes.
  searchedForGames: boolean;
  noGameIdentifier: boolean;
  // This file's own games (per-file game types only). Never shared with
  // other files in the batch.
  gameIds: number[];
  // Set once the user edits games; detection never overwrites them after.
  gamesSetManually: boolean;
  // First certificate code in the document, found by the same content
  // scan as the games. Only used when the file name carries no code —
  // see `resolveCertificateCode`.
  contentCertificateCode?: string;
  // Sticky, content-based lookup of the certificates an approval quotes.
  // Like the game scan, a rename never re-runs it.
  searchedForCertificates: boolean;
  linkedCertificates: LinkedCertificate[];
  // The certificate lookup request failed, so "no match" is unknown;
  // the user can retry it.
  certificateLookupFailed: boolean;
  // Result of the duplicate-path / validation check that runs before upload.
  precheckStatus: PrecheckStatus;
  precheckErrorMessage?: string;
  // Scope (see `fileScopeKey`) the last precheck result was computed for.
  // Upload requires it to match the scope that will actually be sent.
  precheckedScopeKey?: string;
  // Result of the last upload-session-creation attempt for this file.
  uploadError?: UploadError;
  // Per-file jurisdiction, detected from the file name or set by hand.
  marketName?: string;
  marketId?: number;
  marketSource?: ValueSource;
  language?: string;
  languageSource?: ValueSource;
  documentTag?: string;
  // `${selectedFileType}:${file.name}` this file's jurisdiction was last
  // detected for — a memoization guard so unrelated fileItems changes
  // (e.g. adding another file) don't redundantly re-detect this one.
  jurisdictionDetectionKey?: string;
};

type FileScope = {
  document_type: FileType;
  game_ids: number[];
  market_ids: number[];
  partner_ids: number[];
  language?: string;
};

type MissingValues = {
  games: boolean;
  market: boolean;
  language: boolean;
};

const createFileItem = (
  file: File,
  relativePath: string,
): FileListItem => ({
  id: uuidv4(),
  file,
  relativePath,
  searchedForGames: false,
  noGameIdentifier: false,
  gameIds: [],
  gamesSetManually: false,
  searchedForCertificates: false,
  linkedCertificates: [],
  certificateLookupFailed: false,
  precheckStatus: 'init',
});

const fileNameToIconType = (name: string): string | undefined => {
  const i = name.lastIndexOf('.');
  if (i === -1 || i === name.length - 1) return undefined;
  return name.slice(i + 1).toLowerCase();
};

const getFileExtension = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot === -1 || dot === name.length - 1
    ? ''
    : name.slice(dot + 1).toLowerCase();
};

const sessionResultToUploadError = (
  error?: string,
): UploadError | undefined => {
  if (!error) return undefined;
  if (classifyUploadError(error) === 'duplicate') {
    return { status: 'duplicate', message: error };
  }
  return { status: 'error', message: error };
};

const hasMissingValues = (missing: MissingValues | null) =>
  !!missing && (missing.games || missing.market || missing.language);

type GameSelectOption = {
  id: number;
  game_name: string;
};

const ComplianceDocumentsUploadModal = ({
  gameId,
  marketId,
  open,
  onClose,
  onSave,
  initialFile,
  initialShowOperator = false,
  initialShowMarkets = false,
  type = 'Game',
}: {
  gameId?: number;
  marketId?: number;
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  initialFile?: File | null;
  initialShowOperator?: boolean;
  initialShowMarkets?: boolean;
  type?: 'Game' | 'Market';
}) => {
  const [selectedFileType, setSelectedFileType] = useState<
    string | undefined
  >();
  const [selectedOperator, setSelectedOperator] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectedMarkets, setSelectedMarkets] = useState<number[]>(
    initialShowMarkets && marketId !== undefined ? [marketId] : [],
  );
  const [selectedGames, setSelectedGames] = useState<number[]>(
    gameId ? [gameId] : [],
  );
  const [fileItems, setFileItems] = useState<FileListItem[]>([]);
  const { addUpload } = useUploadContext();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isPopperReady, setIsPopperReady] = useState(false);
  const [partnerSearchText, setPartnerSearchText] = useState('');
  const intl = useIntl();

  const selectedFileTypeNormalized = selectedFileType?.toLowerCase();
  const isBrandedReportsFileType =
    selectedFileTypeNormalized === 'branded_report';
  const showOperator =
    initialShowOperator || isBrandedReportsFileType;
  const isJurisdictionDetectionFileType =
    JURISDICTION_DETECTION_FILE_TYPES.includes(
      selectedFileTypeNormalized ?? '',
    );
  const perFileGames = PER_FILE_GAMES_FILE_TYPES.includes(
    selectedFileTypeNormalized ?? '',
  );
  const isGameDetectionFileType = GAME_DETECTION_FILE_TYPES.includes(
    selectedFileTypeNormalized ?? '',
  );
  const perFileMarket = PER_FILE_MARKET_FILE_TYPES.includes(
    selectedFileTypeNormalized ?? '',
  );
  const isCertificateLinkFileType =
    CERTIFICATE_LINK_FILE_TYPES.includes(
      selectedFileTypeNormalized ?? '',
    );
  const isCertificateCodeFileType =
    CERTIFICATE_CODE_FILE_TYPES.includes(
      selectedFileTypeNormalized ?? '',
    );

  const {
    mutate: mutateUploadSessions,
    isPending: isUploadSessionsPending,
    reset: resetUploadSessionsMutation,
  } = useMutation({
    mutationFn: postComplianceDocumentsUploadSessions,
  });

  const { mutate: mutatePrecheck } = useMutation({
    mutationFn: postComplianceDocumentsPrecheck,
  });

  const precheckRequestIdRef = useRef(0);

  // Batch-level Games field; per-file game types carry games on each file.
  const showGames =
    !perFileGames &&
    (!!gameId ||
      GAME_SELECT_FILE_TYPES.includes(
        selectedFileTypeNormalized ?? '',
      ));

  const gamesRequired =
    !perFileGames &&
    GAME_REQUIRED_FILE_TYPES.includes(
      selectedFileTypeNormalized ?? '',
    );

  const { data: filtersData, isLoading: isLoadingFilters } = useQuery(
    {
      queryKey: ['filters'],
      queryFn: fetchFilters,
      enabled: open,
    },
  );

  const { data: gameData } = useQuery({
    queryKey: ['game', gameId],
    queryFn: () => fetchGameById(gameId!),
    enabled: open && showGames && gameId !== undefined,
  });

  const { data: groupGamesData, isLoading: isLoadingGroupGames } =
    useQuery({
      queryKey: ['games', 'game_group', gameData?.game_group],
      queryFn: () =>
        fetchGames({
          game_group: gameData?.game_group
            ? [gameData.game_group]
            : undefined,
        }),
      enabled:
        open &&
        showGames &&
        gameId !== undefined &&
        !!gameData?.game_group,
    });

  const groupGames = useMemo(() => {
    if (groupGamesData?.games?.length) {
      return groupGamesData.games;
    }
    if (gameData) {
      return [gameData];
    }
    return [];
  }, [groupGamesData, gameData]);

  const allGames = useMemo(() => {
    const byId = new Map<number, GameSelectOption>();
    groupGames.forEach((game) => {
      byId.set(game.id, { id: game.id, game_name: game.game_name });
    });
    if (gameId === undefined) {
      filtersData?.games?.forEach((game) => {
        byId.set(game.game_id, {
          id: game.game_id,
          game_name: game.game_name,
        });
      });
    }
    return Array.from(byId.values());
  }, [groupGames, filtersData, gameId]);

  const gameItems = useMemo(
    () =>
      allGames.map((game) => ({
        id: game.id,
        value: game.id,
        label: game.game_name,
        text: game.game_name,
        disabled: game.id === gameId,
      })),
    [allGames, gameId],
  );

  // Per-file game options: every catalogue entry is a game + RTP variant.
  const fileGameItems = useMemo(
    () =>
      [...(filtersData?.games ?? [])]
        .sort((a, b) => a.game_name.localeCompare(b.game_name))
        .map((game) => ({
          id: game.game_id,
          value: game.game_id,
          label: game.game_name,
          text: game.game_name,
        })),
    [filtersData],
  );

  const gameNameById = useMemo(() => {
    const map = new Map<number, string>();
    filtersData?.games?.forEach((game) => {
      map.set(game.game_id, game.game_name);
    });
    return map;
  }, [filtersData]);

  const marketItems =
    filtersData && filtersData.markets
      ? mapConfigToOptions(Markets, filtersData.markets, {
          id: 'market_id',
          name: 'market_name',
        })
      : [];

  const languageItems = useMemo(
    () =>
      mapConfigToOptions(
        Languages,
        filtersData?.languages?.map((language) => ({
          name: language.toLowerCase(),
        })),
      ),
    [filtersData],
  );

  useEffect(() => {
    if (!open) {
      resetUploadSessionsMutation();
    }
  }, [open, resetUploadSessionsMutation]);

  const handlePopperRef = (el: HTMLDivElement | null) => {
    scrollContainerRef.current = el;
    setIsPopperReady(!!el);
  };

  const {
    data: partnersData,
    isLoading: isLoadingPartners,
    isFetchingNextPage,
  } = useInfiniteScroll<PartnerSchema>({
    fetchFn: fetchPartners,
    queryKey: [
      'compliance-upload-operators',
      partnerSearchText,
    ] as const,
    enabled: open && showOperator && isPopperReady,
    params: {
      type: ['operator'],
      ...(partnerSearchText ? { search: partnerSearchText } : {}),
    },
    scrollContainerRef:
      scrollContainerRef as React.RefObject<HTMLElement>,
    pageSize: 20,
    scrollThreshold: 200,
  });

  const fileTypeConfig =
    type === 'Market'
      ? FileTypesComplianceMarket
      : FileTypesComplianceGame;

  const fileTypeItems = mapConstantsToOptions(fileTypeConfig);

  const partnerOptions =
    partnersData?.map((partner) => ({
      id: partner.id,
      value: partner.id,
      label: partner.name,
      text: partner.name,
    })) ?? [];

  const selectedOperatorOptions = useMemo(() => {
    if (!selectedOperator) return undefined;
    return [
      {
        id: selectedOperator.id,
        value: selectedOperator.id,
        label: selectedOperator.name,
      },
    ];
  }, [selectedOperator]);

  const operatorRequired = showOperator && isBrandedReportsFileType;

  const batchGameIds = Array.from(
    new Set([
      ...(gameId !== undefined ? [gameId] : []),
      ...selectedGames,
    ]),
  );
  const batchMarketIds = initialShowMarkets
    ? selectedMarkets
    : marketId !== undefined
      ? [marketId]
      : [];
  const partnerIds =
    operatorRequired && selectedOperator ? [selectedOperator.id] : [];

  // The one place a file's associations are decided. Precheck and the
  // upload session both use it, so a file is checked against exactly the
  // scope it is saved with.
  const buildFileScope = (item: FileListItem): FileScope => ({
    document_type: selectedFileType as FileType,
    game_ids: perFileGames ? item.gameIds : batchGameIds,
    market_ids: perFileMarket
      ? item.marketId !== undefined
        ? [item.marketId]
        : []
      : batchMarketIds,
    partner_ids: partnerIds,
    ...(isJurisdictionDetectionFileType && item.language
      ? { language: item.language }
      : {}),
  });

  // Resolved from the current name, so a rename is picked up.
  const certificateCodeOf = (item: FileListItem) =>
    isCertificateCodeFileType
      ? resolveCertificateCode(
          item.file.name,
          item.contentCertificateCode,
        )
      : undefined;

  const fileScopeKey = (item: FileListItem) =>
    JSON.stringify([item.relativePath, buildFileScope(item)]);

  const precheckKey = selectedFileType
    ? fileItems
        .map((item) => `${item.id}:${fileScopeKey(item)}`)
        .join('|')
    : '';

  useEffect(() => {
    if (fileItems.length === 0 || !selectedFileType) return;

    const requestId = ++precheckRequestIdRef.current;
    const snapshotItems = fileItems;
    const snapshotScopes = snapshotItems.map(buildFileScope);
    const snapshotScopeKeys = snapshotItems.map(fileScopeKey);

    setFileItems((prev) =>
      prev.map((item) => ({
        ...item,
        precheckStatus: 'loading',
        precheckErrorMessage: undefined,
        precheckedScopeKey: undefined,
        uploadError: undefined,
      })),
    );

    mutatePrecheck(
      {
        files: snapshotItems.map((item, index) => {
          const scope = snapshotScopes[index];
          return {
            name: item.relativePath,
            extension: getFileExtension(item.file.name),
            document_type: scope.document_type,
            game_ids: scope.game_ids,
            market_ids: scope.market_ids,
            partner_ids: scope.partner_ids,
          };
        }),
      },
      {
        onSuccess: (data) => {
          if (precheckRequestIdRef.current !== requestId) return;
          setFileItems((prev) =>
            prev.map((item) => {
              const snapshotIndex = snapshotItems.findIndex(
                (s) => s.id === item.id,
              );
              if (snapshotIndex === -1) return item;
              const precheckedScopeKey =
                snapshotScopeKeys[snapshotIndex];
              const result = data.results.find(
                (r) => r.index === snapshotIndex,
              );
              const errorKind = classifyUploadError(result?.error);
              if (errorKind === 'duplicate') {
                return {
                  ...item,
                  precheckStatus: 'duplicate',
                  precheckErrorMessage: undefined,
                  precheckedScopeKey,
                };
              }
              if (errorKind === 'inProgress') {
                return {
                  ...item,
                  precheckStatus: 'error',
                  precheckErrorMessage: intl.formatMessage({
                    id: 'complianceDocumentsUploadModal.uploadInProgress',
                    defaultMessage:
                      'This file is still being uploaded. Wait for it to finish, then try again.',
                  }),
                  precheckedScopeKey,
                };
              }
              if (result?.error) {
                return {
                  ...item,
                  precheckStatus: 'error',
                  precheckErrorMessage: result.error,
                  precheckedScopeKey,
                };
              }
              if (!result) {
                return {
                  ...item,
                  precheckStatus: 'error',
                  precheckErrorMessage: intl.formatMessage({
                    id: 'complianceDocumentsUploadModal.precheckFailed',
                    defaultMessage:
                      'Unable to verify this file. Please try again.',
                  }),
                  precheckedScopeKey,
                };
              }
              return {
                ...item,
                precheckStatus: 'unique',
                precheckErrorMessage: undefined,
                precheckedScopeKey,
              };
            }),
          );
        },
        onError: () => {
          if (precheckRequestIdRef.current !== requestId) return;
          setFileItems((prev) =>
            prev.map((item) => ({
              ...item,
              precheckStatus: 'error',
              precheckErrorMessage: intl.formatMessage({
                id: 'complianceDocumentsUploadModal.precheckFailed',
                defaultMessage:
                  'Unable to verify this file. Please try again.',
              }),
            })),
          );
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [precheckKey]);

  const marketsReady = !!filtersData?.markets;

  // Detection still running for this file, so its values aren't final yet.
  const isDetecting = (item: FileListItem) =>
    (isGameDetectionFileType &&
      (!filtersData || !item.searchedForGames)) ||
    (isCertificateLinkFileType &&
      (!filtersData || !item.searchedForCertificates)) ||
    (isJurisdictionDetectionFileType &&
      (!marketsReady ||
        !item.jurisdictionDetectionKey?.startsWith(
          `${selectedFileTypeNormalized}:`,
        )));

  // Only a warning: it never affects the row's Ready status. Compared
  // against the current name, so a rename re-runs it.
  const certificateCodeMismatchOf = (item: FileListItem) =>
    isCertificateCodeFileType && !isDetecting(item)
      ? findCertificateCodeMismatch(
          item.file.name,
          item.contentCertificateCode,
        )
      : undefined;

  const getMissingValues = (
    item: FileListItem,
  ): MissingValues | null => {
    if (!perFileMarket && !perFileGames) return null;
    if (isDetecting(item)) return null;
    return {
      games: perFileGames && item.gameIds.length === 0,
      market: perFileMarket && item.marketId === undefined,
      language: isJurisdictionDetectionFileType && !item.language,
    };
  };

  const isItemReady = (item: FileListItem) =>
    !isDetecting(item) &&
    !hasMissingValues(getMissingValues(item)) &&
    item.precheckStatus === 'unique' &&
    item.precheckedScopeKey === fileScopeKey(item) &&
    !item.uploadError;

  // Files the user has to act on. Pending prechecks are left out — they
  // settle on their own and naming them would just flicker.
  const notReadyItems = fileItems.filter(
    (item) =>
      !isItemReady(item) &&
      (isDetecting(item) ||
        hasMissingValues(getMissingValues(item)) ||
        item.precheckStatus === 'duplicate' ||
        item.precheckStatus === 'error' ||
        !!item.uploadError),
  );

  const uploadDisabled =
    fileItems.length === 0 ||
    !selectedFileType ||
    (operatorRequired && !selectedOperator) ||
    (gamesRequired && batchGameIds.length === 0) ||
    fileItems.some((item) => !isItemReady(item));

  const missingRequiredFieldLabels = [
    fileItems.length === 0
      ? intl.formatMessage({
          id: 'complianceDocumentsUploadModal.files',
          defaultMessage: 'Files',
        })
      : null,
    !selectedFileType
      ? intl.formatMessage({
          id: 'fileType',
          defaultMessage: 'File Type',
        })
      : null,
    gamesRequired && batchGameIds.length === 0
      ? intl.formatMessage({ id: 'games', defaultMessage: 'Games' })
      : null,
    operatorRequired && !selectedOperator
      ? intl.formatMessage({
          id: 'operator',
          defaultMessage: 'Operator',
        })
      : null,
  ].filter((label): label is string => label !== null);

  const showNotReadyFiles =
    !!selectedFileType && notReadyItems.length > 0;

  const uploadDisabledReason: ReactNode =
    uploadDisabled &&
    (missingRequiredFieldLabels.length > 0 || showNotReadyFiles) ? (
      <>
        {missingRequiredFieldLabels.length > 0 ? (
          <div>
            <FormattedMessage
              id="complianceDocumentsUploadModal.completeRequiredFields"
              defaultMessage="Please complete the following required fields: {fields}"
              values={{
                fields: missingRequiredFieldLabels.join(', '),
              }}
            />
          </div>
        ) : null}
        {showNotReadyFiles ? (
          <div>
            <FormattedMessage
              id="complianceDocumentsUploadModal.filesNotReady"
              defaultMessage="{count, plural, =0 {These files are not ready: {files}} other {These files are not ready: {files} and # more}}"
              values={{
                files: notReadyItems
                  .slice(0, NOT_READY_FILES_NAMED)
                  .map((item) => item.file.name)
                  .join(', '),
                count: Math.max(
                  0,
                  notReadyItems.length - NOT_READY_FILES_NAMED,
                ),
              }}
            />
          </div>
        ) : null}
      </>
    ) : null;

  // Certificate code mismatches sort first, then files needing attention,
  // then Ready ones. The order is only recalculated when files are added
  // or removed, a newly added file finishes detection, or a rename gains
  // or loses a code mismatch — never as the user fills a row in, so the
  // row being worked on stays put.
  const orderKey = [
    selectedFileTypeNormalized,
    fileItems
      .map(
        (item) =>
          `${item.id}:${isDetecting(item) ? 0 : 1}:${
            certificateCodeMismatchOf(item) ? 1 : 0
          }`,
      )
      .join(','),
  ].join('|');

  const orderedIds = useMemo(() => {
    const rank = (item: FileListItem) =>
      certificateCodeMismatchOf(item)
        ? 0
        : isDetecting(item) ||
            hasMissingValues(getMissingValues(item))
          ? 1
          : 2;
    return [0, 1, 2].flatMap((tier) =>
      fileItems
        .filter((item) => rank(item) === tier)
        .map((item) => item.id),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderKey]);

  const orderedFileItems = useMemo(() => {
    const byId = new Map(fileItems.map((item) => [item.id, item]));
    const ordered = orderedIds
      .map((id) => byId.get(id))
      .filter((item): item is FileListItem => !!item);
    const orderedSet = new Set(orderedIds);
    return [
      ...ordered,
      ...fileItems.filter((item) => !orderedSet.has(item.id)),
    ];
  }, [fileItems, orderedIds]);

  useEffect(() => {
    if (initialFile) {
      setFileItems((prev) => {
        if (prev.some((item) => item.file === initialFile)) {
          return prev;
        }
        return [
          ...prev,
          createFileItem(initialFile, initialFile.name),
        ];
      });
    }
  }, [initialFile]);

  useEffect(() => {
    const normalized = selectedFileType?.toLowerCase();
    const branded = normalized === 'branded_report';
    if (!branded) {
      setSelectedOperator(null);
    }
  }, [selectedFileType]);

  useEffect(() => {
    setSelectedFileType(undefined);
  }, [type]);

  useEffect(() => {
    if (open && initialShowMarkets) {
      setSelectedMarkets(marketId !== undefined ? [marketId] : []);
    }
  }, [open, initialShowMarkets, marketId]);

  const handleNext = () => {
    if (uploadDisabled) return;

    const snapshotItems = [...fileItems];

    mutateUploadSessions(
      {
        files: snapshotItems.map((item) => ({
          name: item.relativePath,
          size_bytes: item.file.size,
          extension: getFileExtension(item.file.name),
          mime_type: item.file.type || 'application/octet-stream',
          ...buildFileScope(item),
          ...(isCertificateCodeFileType
            ? { code: certificateCodeOf(item) ?? null }
            : {}),
          ...(isCertificateLinkFileType
            ? { code: item.linkedCertificates[0]?.code ?? null }
            : {}),
        })),
      },
      {
        onSuccess: (data) => {
          const missingResultError: UploadError = {
            status: 'error',
            message: intl.formatMessage({
              id: 'complianceDocumentsUploadModal.uploadSessionsFailed',
              defaultMessage:
                'Upload could not be started. Check the file and folder names, then try again.',
            }),
          };
          const nextItems = snapshotItems.map((item, index) => {
            const result = data.results.find(
              (r) => r.index === index,
            );
            return {
              ...item,
              uploadError: result
                ? sessionResultToUploadError(result.error)
                : missingResultError,
            };
          });

          // A file without a session result would otherwise vanish from
          // the batch and the upload would look successful.
          if (nextItems.some((item) => item.uploadError)) {
            setFileItems(nextItems);
            return;
          }

          const batchId = `batch-${uuidv4()}`;
          const batchFileIds = data.results
            .map((result) => result.file_id)
            .filter(
              (fileId): fileId is number => fileId !== undefined,
            );

          data.results.forEach((result) => {
            const file = snapshotItems[result.index]?.file;
            if (!file) return;

            addUpload({
              file,
              upload_url: result.upload_url ?? '',
              file_id: result.file_id,
              batchId,
              batchFileIds,
              assetDomain: 'compliance_documents',
            });
          });

          onSave();
          handleClose();
        },
        onError: () => {
          setFileItems((prev) =>
            prev.map((item) => ({
              ...item,
              uploadError: {
                status: 'error' as const,
                message: intl.formatMessage({
                  id: 'complianceDocumentsUploadModal.uploadSessionsFailed',
                  defaultMessage:
                    'Upload could not be started. Check the file and folder names, then try again.',
                }),
              },
            })),
          );
        },
      },
    );
  };

  const handleClose = () => {
    if (isUploadSessionsPending) return;
    setFileItems([]);
    setSelectedFileType(undefined);
    setSelectedOperator(null);
    setSelectedMarkets([]);
    setSelectedGames([]);
    setPartnerSearchText('');
    setIsPopperReady(false);
    scrollContainerRef.current = null;
    onClose();
  };

  const detectInFile = async (file: File) => {
    const { games, certificateCode } = await detectCertificateDetails(
      filtersData?.games ?? [],
      file,
    );
    return {
      gameIds: games.map((game) => game.game_id),
      certificateCode,
    };
  };

  const handleFileChange = async (incoming: PickedFile[]) => {
    const canSearchGames = isGameDetectionFileType && !!filtersData;
    const newItems: FileListItem[] = [];

    for (const { file, relativePath } of incoming) {
      const item = createFileItem(file, relativePath);
      if (canSearchGames) {
        const { gameIds, certificateCode } = await detectInFile(file);
        item.searchedForGames = true;
        item.noGameIdentifier = gameIds.length === 0;
        item.gameIds = gameIds;
        item.contentCertificateCode = certificateCode;
      }
      newItems.push(item);
    }

    setFileItems((prev) => [...prev, ...newItems]);
  };

  const fileIdsKey = fileItems.map((item) => item.id).join(',');

  useEffect(() => {
    if (!isGameDetectionFileType || !filtersData) return;

    const unsearchedItems = fileItems.filter(
      (item) => !item.searchedForGames,
    );
    if (unsearchedItems.length === 0) return;

    let cancelled = false;

    (async () => {
      const results = await Promise.all(
        unsearchedItems.map(async (item) => ({
          id: item.id,
          ...(await detectInFile(item.file)),
        })),
      );

      if (cancelled) return;

      setFileItems((prev) =>
        prev.map((item) => {
          const result = results.find((r) => r.id === item.id);
          if (!result) return item;
          return {
            ...item,
            searchedForGames: true,
            noGameIdentifier: result.gameIds.length === 0,
            // Games picked by hand meanwhile are never overwritten.
            gameIds: item.gamesSetManually
              ? item.gameIds
              : result.gameIds,
            contentCertificateCode: result.certificateCode,
          };
        }),
      );
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGameDetectionFileType, filtersData, fileIdsKey]);

  const marketIdByName = useMemo(() => {
    const map = new Map<string, number>();
    filtersData?.markets?.forEach((market) => {
      map.set(market.market_name.toLowerCase(), market.market_id);
    });
    return map;
  }, [filtersData]);

  // Links an approval to the certificates whose codes it quotes and takes
  // their market and games. Without a match, the market comes from a
  // jurisdiction keyword in the text, and the games are left to the user.
  const detectLinkedCertificates = async (file: File) => {
    const { codes, keywordMarketName } =
      await detectApprovalContent(file);

    let certificates: ComplianceDocument[] = [];
    let lookupFailed = false;
    if (codes.length) {
      try {
        certificates = await fetchComplianceDocumentsByCode({
          codes,
          document_type: 'certificates',
        });
      } catch (error) {
        console.error(error);
        lookupFailed = true;
      }
    }

    const keywordMarketId = keywordMarketName
      ? marketIdByName.get(keywordMarketName.toLowerCase())
      : undefined;
    // The row holds a single market; certificates an approval quotes are
    // all for the regulator's one market.
    const certificateMarket = certificates
      .flatMap((certificate) => certificate.markets ?? [])
      .at(0);
    const market = certificateMarket
      ? { id: certificateMarket.id, name: certificateMarket.name }
      : keywordMarketId !== undefined
        ? { id: keywordMarketId, name: keywordMarketName }
        : undefined;

    return {
      certificates: certificates.map(({ id, name, code }) => ({
        id,
        name,
        code,
      })),
      gameIds: [
        ...new Set(
          certificates.flatMap((certificate) =>
            (certificate.games ?? []).map((game) => game.id),
          ),
        ),
      ],
      market,
      lookupFailed,
    };
  };

  // Approvals still waiting for a lookup: new files, and failed ones the
  // user retries.
  const pendingCertificateSearchKey = fileItems
    .filter((item) => !item.searchedForCertificates)
    .map((item) => item.id)
    .join(',');

  useEffect(() => {
    if (!isCertificateLinkFileType || !filtersData) return;

    const unsearchedItems = fileItems.filter(
      (item) => !item.searchedForCertificates,
    );
    if (unsearchedItems.length === 0) return;

    let cancelled = false;

    (async () => {
      const results = await Promise.all(
        unsearchedItems.map(async (item) => ({
          id: item.id,
          ...(await detectLinkedCertificates(item.file)),
        })),
      );

      if (cancelled) return;

      setFileItems((prev) =>
        prev.map((item): FileListItem => {
          const result = results.find((r) => r.id === item.id);
          if (!result) return item;
          // Values picked by hand meanwhile are never overwritten.
          const keepMarket = item.marketSource === 'manual';
          return {
            ...item,
            searchedForCertificates: true,
            linkedCertificates: result.certificates,
            certificateLookupFailed: result.lookupFailed,
            gameIds: item.gamesSetManually
              ? item.gameIds
              : result.gameIds,
            ...(keepMarket
              ? {}
              : {
                  marketId: result.market?.id,
                  marketName: result.market?.name,
                  marketSource: result.market
                    ? 'detected'
                    : undefined,
                }),
          };
        }),
      );
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCertificateLinkFileType,
    filtersData,
    pendingCertificateSearchKey,
  ]);

  const handleRetryCertificateLookup = (fileId: string) => {
    updateFileItem(fileId, (item) => ({
      ...item,
      searchedForCertificates: false,
      certificateLookupFailed: false,
    }));
  };

  const fileNamesKey = fileItems
    .map((item) => `${item.id}:${item.file.name}`)
    .join('|');

  useEffect(() => {
    if (!isJurisdictionDetectionFileType) return;

    setFileItems((prev) => {
      let changed = false;
      const next = prev.map((item): FileListItem => {
        const detectionKey = `${selectedFileTypeNormalized}:${item.file.name}:${marketsReady ? 'ready' : 'loading'}`;
        if (item.jurisdictionDetectionKey === detectionKey) {
          return item;
        }
        changed = true;
        const detected = detectFileJurisdiction(item.file.name);
        const detectedMarketId = detected.marketName
          ? marketIdByName.get(detected.marketName.toLowerCase())
          : undefined;

        // Market and language come from the name, so a value resolved
        // from it replaces the current one, a hand-picked one included.
        if (
          detected.marketName &&
          (detectedMarketId !== undefined || !marketsReady)
        ) {
          return {
            ...item,
            marketName: detected.marketName,
            marketId: detectedMarketId,
            marketSource: 'detected',
            language: detected.language,
            languageSource: detected.language
              ? 'detected'
              : undefined,
            documentTag: detected.documentTag,
            jurisdictionDetectionKey: detectionKey,
          };
        }

        // Nothing resolved: keep hand-picked values, drop detected ones.
        const keepMarket = item.marketSource === 'manual';
        const keepLanguage = item.languageSource === 'manual';
        return {
          ...item,
          marketName: keepMarket ? item.marketName : undefined,
          marketId: keepMarket ? item.marketId : undefined,
          marketSource: keepMarket ? 'manual' : undefined,
          language: keepLanguage ? item.language : undefined,
          languageSource: keepLanguage ? 'manual' : undefined,
          documentTag: detected.documentTag,
          jurisdictionDetectionKey: detectionKey,
        };
      });
      return changed ? next : prev;
    });
  }, [
    isJurisdictionDetectionFileType,
    selectedFileTypeNormalized,
    fileNamesKey,
    marketsReady,
    marketIdByName,
  ]);

  const updateFileItem = (
    fileId: string,
    update: (item: FileListItem) => FileListItem,
  ) => {
    setFileItems((prev) =>
      prev.map((item) => (item.id === fileId ? update(item) : item)),
    );
  };

  const handleFileGamesChange = (fileId: string, ids: number[]) => {
    updateFileItem(fileId, (item) => ({
      ...item,
      gameIds: ids,
      gamesSetManually: true,
    }));
  };

  const handleFileMarketChange = (
    fileId: string,
    nextMarketId: number | null,
  ) => {
    updateFileItem(fileId, (item) => {
      if (nextMarketId === null) {
        return {
          ...item,
          marketId: undefined,
          marketName: undefined,
          marketSource: undefined,
        };
      }
      const marketName = filtersData?.markets?.find(
        (market) => market.market_id === nextMarketId,
      )?.market_name;
      if (!marketName) return item;
      // Fills language by the same rule detection uses.
      return {
        ...item,
        marketId: nextMarketId,
        marketName,
        marketSource: 'manual',
        language: languageForMarket(marketName, item.file.name),
        languageSource: 'manual',
      };
    });
  };

  const handleFileLanguageChange = (
    fileId: string,
    nextLanguage: string | null,
  ) => {
    updateFileItem(fileId, (item) => ({
      ...item,
      language: nextLanguage ?? undefined,
      languageSource: nextLanguage ? 'manual' : undefined,
    }));
  };

  const handleRemoveFile = (fileId: string) => {
    setFileItems((prev) => prev.filter((f) => f.id !== fileId));
  };

  const formatMissingValuesAction = (missing: MissingValues) => {
    const parts = [
      missing.games
        ? intl.formatMessage({
            id: 'complianceDocumentsUploadModal.missingValue.game',
            defaultMessage: 'a game',
          })
        : null,
      missing.market
        ? intl.formatMessage({
            id: 'complianceDocumentsUploadModal.missingValue.market',
            defaultMessage: 'a market',
          })
        : null,
      missing.language
        ? intl.formatMessage({
            id: 'complianceDocumentsUploadModal.missingValue.language',
            defaultMessage: 'a language',
          })
        : null,
    ].filter((part): part is string => part !== null);

    const values =
      parts.length > 1
        ? intl.formatMessage(
            {
              id: 'complianceDocumentsUploadModal.missingValue.list',
              defaultMessage: '{items} and {last}',
            },
            {
              items: parts.slice(0, -1).join(', '),
              last: parts[parts.length - 1],
            },
          )
        : parts[0];

    return intl.formatMessage(
      {
        id: 'complianceDocumentsUploadModal.selectMissingValues',
        defaultMessage: 'Select {values} to upload this file.',
      },
      { values },
    );
  };

  const renderReadOnlyValue = (icon: ReactNode, label: ReactNode) => (
    <Tooltip
      title={intl.formatMessage({
        id: 'complianceDocumentsUploadModal.autodetectedValueTooltip',
        defaultMessage:
          'This value was autodetected from filename. To change it, change filename.',
      })}
      placement="top"
    >
      <ReadOnlyField aria-readonly tabIndex={0}>
        {icon}
        <ReadOnlyFieldText>{label}</ReadOnlyFieldText>
      </ReadOnlyField>
    </Tooltip>
  );

  const renderMarketValue = (marketName: string) =>
    renderReadOnlyValue(
      Markets[marketName]?.icon,
      Markets[marketName]?.label ?? marketName,
    );

  const renderLanguageValue = (language: string) =>
    renderReadOnlyValue(
      Languages[language.toLowerCase()]?.icon,
      Languages[language.toLowerCase()]?.label ?? language,
    );

  const renderFileDetails = (item: FileListItem) => {
    // An approval's market is pre-filled from its certificates or its
    // text, never from its name, so it stays editable.
    const marketEditable =
      isCertificateLinkFileType || item.marketSource !== 'detected';
    const languageEditable =
      !isJurisdictionDetectionFileType ||
      item.languageSource !== 'detected';
    const certificateCode = certificateCodeOf(item);

    return (
      <FileDetails>
        {!marketEditable || !languageEditable ? (
          <FileHint>
            <FormattedMessage
              id="complianceDocumentsUploadModal.renameToChangeJurisdiction"
              defaultMessage="Rename the file to change its market and language."
            />
          </FileHint>
        ) : null}
        <FileDetailColumns $columns={2}>
          <FileDetailColumn>
            <FileDetailLabel>
              <FormattedMessage
                id="complianceDocumentsUploadModal.market"
                defaultMessage="Market"
              />
            </FileDetailLabel>
            <FileDetailValue>
              {marketEditable ? (
                <Select
                  multiple={false}
                  placeholder={
                    <FormattedMessage
                      id="complianceDocumentsUploadModal.selectMarket"
                      defaultMessage="Select market"
                    />
                  }
                  options={marketItems}
                  value={item.marketId ?? null}
                  onChange={(v) =>
                    handleFileMarketChange(
                      item.id,
                      v as number | null,
                    )
                  }
                  variant="outlined"
                  searchEnabled
                  size="small"
                  disabled={isUploadSessionsPending}
                  aria-label={intl.formatMessage({
                    id: 'complianceDocumentsUploadModal.selectMarket',
                    defaultMessage: 'Select market',
                  })}
                />
              ) : item.marketName ? (
                renderMarketValue(item.marketName)
              ) : null}
            </FileDetailValue>
          </FileDetailColumn>
          {isJurisdictionDetectionFileType ? (
            <FileDetailColumn>
              <FileDetailLabel>
                <FormattedMessage
                  id="complianceDocumentsUploadModal.language"
                  defaultMessage="Language"
                />
              </FileDetailLabel>
              <FileDetailValue>
                {languageEditable ? (
                  <Select
                    multiple={false}
                    placeholder={
                      <FormattedMessage
                        id="complianceDocumentsUploadModal.selectLanguage"
                        defaultMessage="Select language"
                      />
                    }
                    options={languageItems}
                    value={item.language ?? null}
                    onChange={(v) =>
                      handleFileLanguageChange(
                        item.id,
                        v as string | null,
                      )
                    }
                    variant="outlined"
                    searchEnabled
                    size="small"
                    disabled={isUploadSessionsPending}
                    aria-label={intl.formatMessage({
                      id: 'complianceDocumentsUploadModal.selectLanguage',
                      defaultMessage: 'Select language',
                    })}
                  />
                ) : item.language ? (
                  renderLanguageValue(item.language)
                ) : null}
              </FileDetailValue>
            </FileDetailColumn>
          ) : null}
        </FileDetailColumns>
        {isCertificateLinkFileType &&
        item.linkedCertificates.length ? (
          <FileDetailColumn>
            <FileDetailLabel>
              <FormattedMessage
                id="complianceDocumentsUploadModal.linkedCertificates"
                defaultMessage="Linked certificates"
              />
            </FileDetailLabel>
            <FileBadgesRow>
              {item.linkedCertificates.map((certificate) => (
                <MetaBadge key={certificate.id}>
                  {certificate.name}
                </MetaBadge>
              ))}
            </FileBadgesRow>
          </FileDetailColumn>
        ) : null}
        {perFileGames ? (
          <FileDetailColumn>
            <FileDetailLabel>
              <FormattedMessage id="games" defaultMessage="Games" />
            </FileDetailLabel>
            <Select
              multiple
              placeholder={
                item.gameIds.length ? (
                  <FormattedMessage
                    id="complianceDocumentsUploadModal.addGame"
                    defaultMessage="Add game"
                  />
                ) : (
                  <FormattedMessage
                    id="complianceDocumentsUploadModal.selectGames"
                    defaultMessage="Select games"
                  />
                )
              }
              options={fileGameItems}
              value={item.gameIds}
              // The chips show the selection, so the head only
              // shows the placeholder.
              selectedOptions={[]}
              onChange={(ids) =>
                handleFileGamesChange(item.id, ids as number[])
              }
              variant="outlined"
              searchEnabled
              size="small"
              disableSelectAll
              showClear={false}
              listWidth={300}
              disabled={isUploadSessionsPending}
              aria-label={intl.formatMessage({
                id: 'complianceDocumentsUploadModal.selectGames',
                defaultMessage: 'Select games',
              })}
            />
            {/* Every game is a chip, so none hide behind "N more". */}
            {item.gameIds.length ? (
              <GameChips>
                {item.gameIds.map((id) => {
                  const name = gameNameById.get(id) ?? String(id);
                  return (
                    <FilterChip
                      key={id}
                      label={name}
                      onRemove={() => {
                        if (isUploadSessionsPending) return;
                        handleFileGamesChange(
                          item.id,
                          item.gameIds.filter((g) => g !== id),
                        );
                      }}
                      aria-label={intl.formatMessage(
                        {
                          id: 'complianceDocumentsUploadModal.removeGame',
                          defaultMessage: 'Remove {game}',
                        },
                        { game: name },
                      )}
                    />
                  );
                })}
              </GameChips>
            ) : null}
          </FileDetailColumn>
        ) : null}
        {item.documentTag || certificateCode ? (
          <FileBadgesRow>
            {item.documentTag ? (
              <MetaBadge>{item.documentTag}</MetaBadge>
            ) : null}
            {certificateCode ? (
              <MetaBadge>
                <FormattedMessage
                  id="complianceDocumentsUploadModal.certificateCode"
                  defaultMessage="Code: {code}"
                  values={{ code: certificateCode }}
                />
              </MetaBadge>
            ) : null}
          </FileBadgesRow>
        ) : null}
      </FileDetails>
    );
  };

  return (
    <StyledModal
      open={open}
      onClose={() => {
        if (isUploadSessionsPending) return;
        handleClose();
      }}
      isActionBusy={isUploadSessionsPending}
      title="Upload Compliance Documents"
      actions={[
        <Button
          key="cancel"
          variant="text"
          onClick={handleClose}
          disabled={isUploadSessionsPending}
          loading={isUploadSessionsPending}
          aria-label="Cancel Button"
        >
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>,
        <Tooltip
          key="next"
          title={uploadDisabledReason ?? ''}
          disableHoverListener={!uploadDisabled}
          disableFocusListener={!uploadDisabled}
          disableTouchListener={!uploadDisabled}
        >
          <span style={{ display: 'inline-flex' }}>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={uploadDisabled || isUploadSessionsPending}
              loading={isUploadSessionsPending}
              aria-label={intl.formatMessage({
                id: 'complianceDocumentsUploadModal.upload',
                defaultMessage: 'Upload',
              })}
            >
              <FormattedMessage
                id="complianceDocumentsUploadModal.upload"
                defaultMessage="Upload"
              />
            </Button>
          </span>
        </Tooltip>,
      ]}
    >
      <Subtitle>
        <FormattedMessage
          id="complianceDocumentsUploadModal.subtitle"
          defaultMessage="Choose the file type and market and add the files you would like to upload"
        />
      </Subtitle>

      <TwoColumnRow>
        <Column>
          <Label>
            <FormattedMessage
              id="fileType"
              defaultMessage="File Type"
            />
            <Asterix>*</Asterix>
          </Label>
          <Select
            multiple={false}
            placeholder={
              <FormattedMessage id="select" defaultMessage="Select" />
            }
            options={fileTypeItems}
            value={selectedFileType}
            onChange={(v) => setSelectedFileType(v as string)}
            aria-label="File Type"
            variant="outlined"
            searchEnabled
            size="small"
            disabled={isUploadSessionsPending}
          />
        </Column>
        {showOperator ? (
          <Column>
            <Label>
              <FormattedMessage
                id="operator"
                defaultMessage="Operator"
              />
              {operatorRequired ? <Asterix>*</Asterix> : null}
            </Label>
            <Select
              ref={handlePopperRef}
              multiple={false}
              placeholder={
                <FormattedMessage
                  id="complianceDocumentsUploadModal.selectOperator"
                  defaultMessage="Select operator"
                />
              }
              options={partnerOptions}
              selectedOptions={selectedOperatorOptions}
              value={selectedOperator?.id ?? null}
              onChange={(v) => {
                if (v === null) {
                  setSelectedOperator(null);
                  return;
                }
                const id = v as number;
                const partner = partnersData?.find(
                  (p) => p.id === id,
                );
                setSelectedOperator({
                  id,
                  name: partner?.name ?? String(id),
                });
              }}
              variant="outlined"
              searchEnabled
              size="small"
              loading={isLoadingPartners}
              loadingNextPage={isFetchingNextPage}
              onSearchChange={setPartnerSearchText}
              disableLocalFiltering
              aria-label="Operator"
              disabled={isUploadSessionsPending}
            />
          </Column>
        ) : null}
      </TwoColumnRow>

      {showGames ? (
        <Row>
          <Column>
            <Label>
              <FormattedMessage id="games" defaultMessage="Games" />
              {gamesRequired ? <Asterix>*</Asterix> : null}
            </Label>
            <Select
              multiple
              placeholder={
                (
                  gameId !== undefined
                    ? isLoadingGroupGames
                    : isLoadingFilters
                ) ? (
                  <FormattedMessage
                    id="loading"
                    defaultMessage="Loading..."
                  />
                ) : (
                  <FormattedMessage
                    id="select"
                    defaultMessage="Select"
                  />
                )
              }
              options={
                (
                  gameId !== undefined
                    ? isLoadingGroupGames
                    : isLoadingFilters
                )
                  ? []
                  : gameItems
              }
              value={selectedGames}
              onChange={(v) => setSelectedGames(v as number[])}
              aria-label="Games"
              variant="outlined"
              searchEnabled
              size="small"
              disableSelectAll
              disabled={isUploadSessionsPending}
            />
          </Column>
        </Row>
      ) : null}

      {initialShowMarkets && !perFileMarket ? (
        <Row>
          <Column>
            <Label>
              <FormattedMessage
                id="selectMarkets"
                defaultMessage="Market"
              />
            </Label>
            <Select
              multiple
              placeholder={
                isLoadingFilters ? (
                  <FormattedMessage
                    id="loading"
                    defaultMessage="Loading..."
                  />
                ) : (
                  <FormattedMessage
                    id="select"
                    defaultMessage="Select"
                  />
                )
              }
              options={isLoadingFilters ? [] : marketItems}
              value={selectedMarkets}
              onChange={(v) => setSelectedMarkets(v as number[])}
              aria-label="Market"
              variant="outlined"
              searchEnabled
              size="small"
              disabled={isUploadSessionsPending}
            />
          </Column>
        </Row>
      ) : null}

      <DropZoneSection>
        <DropZoneBig
          acceptedExtensions={[...ComplianceUploadAcceptedExtensions]}
          busy={isUploadSessionsPending}
          allowDirectories
          onFilesChange={handleFileChange}
        >
          {fileItems.length > 0 ? (
            <>
              <FilesCounter>
                <FormattedMessage
                  id="complianceDocumentsUploadModal.filesAdded"
                  defaultMessage="{count, plural, one {# file added} other {# files added}}"
                  values={{ count: fileItems.length }}
                />
              </FilesCounter>
              <FileList>
                {orderedFileItems.map((item) => {
                  const isLoading =
                    item.precheckStatus === 'loading' ||
                    isDetecting(item);
                  const isAwaitingFileType = !selectedFileType;
                  const missing = getMissingValues(item);
                  const showActionNeeded =
                    hasMissingValues(missing) && !isAwaitingFileType;
                  const blockingError: UploadError | undefined =
                    item.uploadError ??
                    (item.precheckStatus === 'duplicate'
                      ? { status: 'duplicate' }
                      : item.precheckStatus === 'error'
                        ? {
                            status: 'error',
                            message: item.precheckErrorMessage,
                          }
                        : undefined);
                  // A duplicate and missing values are shown together, so
                  // fixing one never reveals the other only afterwards.
                  const showBlockingError =
                    !!blockingError &&
                    !isAwaitingFileType &&
                    item.precheckStatus !== 'loading';
                  // Not blocking: the user can still set market and games
                  // by hand, but is told the match is unknown.
                  const showLookupError =
                    isCertificateLinkFileType &&
                    item.certificateLookupFailed &&
                    !isDetecting(item);
                  const showDetails =
                    perFileMarket &&
                    !isAwaitingFileType &&
                    !isDetecting(item);
                  const codeMismatch =
                    certificateCodeMismatchOf(item);

                  return (
                    <FileListRow key={item.id}>
                      <FileCard>
                        <FileRowContent>
                          <FileIconSlot $spanRows={showDetails}>
                            <FileIcon
                              fileType={fileNameToIconType(
                                item.file.name,
                              )}
                              size={28}
                            />
                          </FileIconSlot>
                          <FileNameColumn>
                            {folderPartOf(item.relativePath) ? (
                              <FolderPath>
                                {`${folderPartOf(item.relativePath)}/`}
                              </FolderPath>
                            ) : null}
                            <EditableFileName
                              fileName={item.file.name}
                              disabled={isUploadSessionsPending}
                              onSave={(newName) => {
                                updateFileItem(item.id, (entry) => ({
                                  ...entry,
                                  file: renameFile(
                                    entry.file,
                                    newName,
                                  ),
                                  relativePath: withRenamedFile(
                                    entry.relativePath,
                                    newName,
                                  ),
                                  precheckStatus: 'init',
                                  precheckErrorMessage: undefined,
                                  precheckedScopeKey: undefined,
                                  uploadError: undefined,
                                }));
                              }}
                            />
                            {showBlockingError ||
                            showActionNeeded ||
                            showLookupError ||
                            codeMismatch ? (
                              <>
                                {codeMismatch ? (
                                  <FileMetaError>
                                    <FileMetaWarningIcon
                                      width={16}
                                      height={16}
                                      aria-hidden
                                    />
                                    <FileActionText>
                                      <strong>
                                        <FormattedMessage
                                          id="complianceDocumentsUploadModal.certificateCodeMismatch"
                                          defaultMessage="Certificate code mismatch"
                                        />
                                      </strong>
                                      {' — '}
                                      <FormattedMessage
                                        id="complianceDocumentsUploadModal.certificateCodeMismatchDetails"
                                        defaultMessage="File name: {fileNameCode} · Document: {documentCode}. Rename the file to match the document, or remove it."
                                        values={codeMismatch}
                                      />
                                    </FileActionText>
                                  </FileMetaError>
                                ) : null}
                                {showLookupError ? (
                                  <FileMetaError>
                                    <FileMetaWarningIcon
                                      width={16}
                                      height={16}
                                      aria-hidden
                                    />
                                    <FileActionText>
                                      <FormattedMessage
                                        id="complianceDocumentsUploadModal.certificateLookupFailed"
                                        defaultMessage="Could not check certificates."
                                      />{' '}
                                      <RetryButton
                                        type="button"
                                        disabled={
                                          isUploadSessionsPending
                                        }
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRetryCertificateLookup(
                                            item.id,
                                          );
                                        }}
                                      >
                                        <FormattedMessage
                                          id="complianceDocumentsUploadModal.retry"
                                          defaultMessage="Retry"
                                        />
                                      </RetryButton>
                                    </FileActionText>
                                  </FileMetaError>
                                ) : null}
                                {showBlockingError ? (
                                  <FileMetaError>
                                    <FileMetaWarningIcon
                                      width={16}
                                      height={16}
                                      aria-hidden
                                    />
                                    {blockingError?.status ===
                                    'duplicate' ? (
                                      <FormattedMessage
                                        id="complianceDocumentsUploadModal.duplicateFileError"
                                        defaultMessage="This file already exists. Rename or remove it to continue with the upload."
                                      />
                                    ) : (
                                      <span>
                                        {blockingError?.message}
                                      </span>
                                    )}
                                  </FileMetaError>
                                ) : null}
                                {showActionNeeded && missing ? (
                                  <FileMetaError>
                                    <FileMetaWarningIcon
                                      width={16}
                                      height={16}
                                      aria-hidden
                                    />
                                    <FileActionText>
                                      {missing.games &&
                                      isGameDetectionFileType &&
                                      item.noGameIdentifier ? (
                                        <>
                                          <FormattedMessage
                                            id="complianceDocumentsUploadModal.noGameIdentifierFound"
                                            defaultMessage="No game identifier found in this file."
                                          />{' '}
                                        </>
                                      ) : null}
                                      {missing.games &&
                                      isCertificateLinkFileType &&
                                      !item.certificateLookupFailed &&
                                      !item.linkedCertificates
                                        .length ? (
                                        <>
                                          <FormattedMessage
                                            id="complianceDocumentsUploadModal.noLinkedCertificateFound"
                                            defaultMessage="No uploaded certificate matches this file."
                                          />{' '}
                                        </>
                                      ) : null}
                                      {formatMissingValuesAction(
                                        missing,
                                      )}
                                    </FileActionText>
                                  </FileMetaError>
                                ) : null}
                              </>
                            ) : (
                              <FileMeta>
                                {formatFileSize(item.file.size) ?? ''}
                              </FileMeta>
                            )}
                          </FileNameColumn>
                          <FileRowEndActions>
                            {isLoading ? (
                              <CircularProgress size={16} />
                            ) : isAwaitingFileType ? (
                              <FileStatusBadge state="Waiting" />
                            ) : showActionNeeded ? (
                              <FileStatusBadge state="ActionNeeded" />
                            ) : blockingError?.status ===
                              'error' ? null : (
                              <FileStatusBadge
                                state={
                                  blockingError?.status ===
                                  'duplicate'
                                    ? 'Duplicate'
                                    : 'Ready'
                                }
                              />
                            )}
                            <RemoveFileButton
                              type="button"
                              disabled={isUploadSessionsPending}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFile(item.id);
                              }}
                              aria-label={intl.formatMessage({
                                id: 'complianceDocumentsUploadModal.removeFile',
                                defaultMessage: 'Remove file',
                              })}
                            >
                              <DeleteCircleIcon
                                width={24}
                                height={24}
                                aria-hidden
                              />
                            </RemoveFileButton>
                          </FileRowEndActions>
                          {showDetails ? (
                            <FileDetailsRow>
                              {renderFileDetails(item)}
                            </FileDetailsRow>
                          ) : null}
                        </FileRowContent>
                      </FileCard>
                    </FileListRow>
                  );
                })}
              </FileList>
            </>
          ) : null}
        </DropZoneBig>
      </DropZoneSection>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)({
  width: '90%',
  maxWidth: '800px',
});

const TwoColumnRow = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
}));

const Row = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const DropZoneSection = styled('div')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(3),
}));

const Column = styled('div')({
  flex: 1,
  minWidth: 0,
});

const Label = styled('div')(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  marginBottom: theme.spacing(1),
}));

const Asterix = styled('span')(({ theme }) => ({
  color: theme.palette.red600,
  display: 'inline-block',
  marginLeft: theme.spacing(0.5),
}));

const Subtitle = styled('div')(({ theme }) => ({
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: 400,
  color: theme.palette.textSecondaryDarkGrey,
  marginBottom: theme.spacing(2),
}));

const FilesCounter = styled('div')(({ theme }) => ({
  fontSize: '14px',
  lineHeight: '20px',
  color: theme.palette.textSecondaryDarkGrey,
  marginBottom: theme.spacing(1.5),
}));

const FileList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const FileListRow = styled('li')({
  margin: 0,
  padding: 0,
});

const FileCard = styled('div')(({ theme }) => ({
  padding: '16px',
  borderRadius: theme.radii.md,
  border: `1px solid ${theme.palette.grey200}`,
}));

// icon | name + meta | badge + actions, with optional details
// (selects) on a second row spanning everything after the icon.
const FileRowContent = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
  alignItems: 'start',
  columnGap: theme.spacing(2),
  rowGap: theme.spacing(1.5),
  minWidth: 0,
  fontSize: '14px',
  lineHeight: '20px',
  color: theme.palette.textPrimaryBlack,
}));

const FileIconSlot = styled('span')<{ $spanRows?: boolean }>(
  ({ theme, $spanRows = false }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    marginTop: $spanRows ? 0 : theme.spacing(0.25),
    // Full height of the card when the details row is present.
    gridRow: $spanRows ? '1 / span 2' : undefined,
    alignSelf: 'start',
  }),
);

const FileNameColumn = styled('div')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const FileMeta = styled('span')(({ theme }) => ({
  fontSize: '12px',
  lineHeight: '16px',
  color: theme.palette.secDefault,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

/** Shown in every row state, unlike the size meta. */
const FolderPath = styled('span')(({ theme }) => ({
  fontSize: '12px',
  lineHeight: '16px',
  color: theme.palette.black40o,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const FileMetaError = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  minWidth: 0,
  fontSize: '12px',
  lineHeight: '16px',
  color: theme.palette.textWarning,
  '& span': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const FileMetaWarningIcon = styled(WarningTriangleIcon)({
  flexShrink: 0,
  display: 'block',
  '& path': {
    stroke: 'currentColor',
  },
});

const FileBadgesRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(0.5),
}));

const MetaBadge = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: `${theme.spacing(0.25)} ${theme.spacing(1)}`,
  borderRadius: theme.radii.xxs,
  backgroundColor: theme.palette.grey100,
  color: theme.palette.textSecondaryDarkGrey,
  fontSize: '10px',
  fontWeight: 600,
  lineHeight: '16px',
  '& svg': {
    width: 12,
    height: 12,
    flexShrink: 0,
  },
}));

const FileActionText = styled('div')({
  minWidth: 0,
});

const RetryButton = styled('button')(({ theme }) => ({
  padding: 0,
  border: 'none',
  background: 'none',
  font: 'inherit',
  fontWeight: 600,
  color: 'inherit',
  textDecoration: 'underline',
  cursor: 'pointer',
  '&:disabled': {
    cursor: 'default',
    opacity: 0.45,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.textWarning}`,
    outlineOffset: 2,
  },
}));

const FileDetailsRow = styled('div')({
  gridColumn: '2 / -1',
  minWidth: 0,
});

const FileDetails = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

// Games, market and language side by side: label above, value below.
const FileDetailColumns = styled('div')<{ $columns: number }>(
  ({ theme, $columns }) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${$columns}, minmax(0, 1fr))`,
    gap: theme.spacing(1.5),
    // Selects line up on one baseline even if a column has no label.
    alignItems: 'end',
  }),
);

const GameChips = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(0.5),
}));

const FileDetailColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  minWidth: 0,
}));

const FileDetailLabel = styled('div')(({ theme }) => ({
  fontSize: '12px',
  lineHeight: '16px',
  fontWeight: 500,
  color: theme.palette.textSecondaryDarkGrey,
}));

const FileDetailValue = styled('div')(({ theme }) => ({
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  // The shared Select has a 350px min width, too wide for a column.
  '& > div': {
    minWidth: 0,
  },
}));

// Detected market/language: sized like the Select head so the
// columns stay even, but muted to read as not editable.
const ReadOnlyField = styled('div')(({ theme }) => ({
  boxSizing: 'border-box',
  height: '48px',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: '12px',
  border: `1px solid ${theme.palette.grey200}`,
  borderRadius: theme.radii.xs,
  backgroundColor: theme.palette.grey50,
  color: theme.palette.textSecondaryDarkGrey,
  fontSize: '16px',
  lineHeight: '24px',
  '& svg': {
    width: 16,
    height: 16,
    flexShrink: 0,
  },
}));

const ReadOnlyFieldText = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const FileHint = styled('span')(({ theme }) => ({
  fontSize: '12px',
  lineHeight: '16px',
  color: theme.palette.black40o,
}));

const FileRowEndActions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  alignSelf: 'flex-start',
  gap: theme.spacing(0.5),
}));

const RemoveFileButton = styled('button')(({ theme }) => ({
  flexShrink: 0,
  margin: 0,
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
  borderRadius: theme.radii.xs,
  color: theme.palette.iconGrey,
  '& path': {
    stroke: 'currentColor',
  },
  '&:hover:not(:disabled)': {
    color: theme.palette.grey600,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary500}`,
    outlineOffset: 2,
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.45,
  },
}));

export default ComplianceDocumentsUploadModal;
